import type { NativeAccountRemoval } from '@/lib/utils/native-credential-store'

export class DesktopAccountCleanupError extends Error {
  constructor(readonly unattributed = false) {
    super(
      'The saved sign-in was removed. Local account cleanup still needs to finish before a new sign-in.',
    )
    this.name = 'DesktopAccountCleanupError'
  }
}

/** Only native receipts authorize cleanup. Never dispatch server deletion/logout. */
export function createDesktopAccountCleanup(options: {
  pending(): Promise<NativeAccountRemoval[]>
  cleanup(receipt: NativeAccountRemoval): Promise<void>
  complete(operationId: string): Promise<void>
}) {
  const running = new Map<string, Promise<void>>()
  const run = (receipt: NativeAccountRemoval): Promise<void> => {
    if (receipt.completed) return Promise.resolve()
    const known = running.get(receipt.operationId)
    if (known) return known
    const operation = (async () => {
      const current = (await options.pending()).find(
        item => item.operationId === receipt.operationId,
      )
      if (!current) return // Another window already completed this operation.
      if (
        current.completed ||
        current.accountId !== receipt.accountId ||
        current.loginFamilyId !== receipt.loginFamilyId ||
        current.ownerId !== receipt.ownerId
      )
        throw new DesktopAccountCleanupError()
      await options.cleanup(current)
      await options.complete(current.operationId)
    })().catch(() => {
      throw new DesktopAccountCleanupError()
    })
    running.set(receipt.operationId, operation)
    void operation
      .finally(() => {
        if (running.get(receipt.operationId) === operation)
          running.delete(receipt.operationId)
      })
      .catch(() => undefined)
    return operation
  }
  return {
    run,
    async resume(): Promise<{ unattributed: boolean }> {
      const receipts = await options.pending()
      let failed = false
      let unattributed = false
      for (const receipt of receipts) {
        try {
          await run(receipt)
          if (receipt.ownerId === null) unattributed = true
        } catch {
          failed = true
        }
      }
      if (failed) throw new DesktopAccountCleanupError(unattributed)
      return { unattributed }
    },
  }
}
